import { useCallback, useRef, useState } from 'react';
import useCards, { Card, CardMediaType } from '@/hooks/cards/useCards';
import { unzip } from 'fflate';

type RestoreStep =
    | 'none'
    | 'readingZip'
    | 'parsingJson'
    | 'restoringMedia'
    | 'done';

export const useRestore = () => {
    const { storeMedia } = useCards();

    const isRestoring = useRef(false);
    const [restoreStep, setRestoreStep] = useState<RestoreStep>('none');
    const [restoreProgress, setRestoreProgress] = useState(0);
    const [restoredCount, setRestoredCount] = useState(0);
    const [skippedCount, setSkippedCount] = useState(0);

    const bytesToDataUrl = useCallback(async (bytes: Uint8Array, mime: string) => {
        // Ensure Blob gets a proper ArrayBuffer (not SharedArrayBuffer)
        const buf: ArrayBuffer =
            bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
                ? (bytes.buffer as ArrayBuffer)
                : (bytes.buffer as ArrayBuffer).slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
        const blob = new Blob([buf], { type: mime });
        const dataUrl: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
            reader.onload = () => resolve(String(reader.result));
            reader.readAsDataURL(blob);
        });
        return dataUrl;
    }, []);

    const restoreFromFiles = useCallback(
        async (files: FileList | File[]) => {
            if (isRestoring.current) return;
            try {
                isRestoring.current = true;
                setRestoreStep('readingZip');
                setRestoreProgress(0.01);
                setRestoredCount(0);
                setSkippedCount(0);

                // Normalize to array
                const fileArr = Array.from(files as FileList);

                // Iterate files sequentially to minimize memory spikes
                let totalItems = 0;
                const perFileData: {
                    cards: Card[];
                    entries: Record<string, Uint8Array>;
                }[] = [];

                for (let fi = 0; fi < fileArr.length; fi++) {
                    const f = fileArr[fi];
                    const ab = await f.arrayBuffer();
                    const u8 = new Uint8Array(ab);

                    const entries: Record<string, Uint8Array> = await new Promise(
                        (resolve, reject) => {
                            unzip(u8, (err, data) => {
                                if (err) reject(err);
                                else resolve(data as Record<string, Uint8Array>);
                            });
                        },
                    );

                    setRestoreStep('parsingJson');
                    const dataJsonEntry = Object.keys(entries).find(
                        (k) => k.toLowerCase().endsWith('data.json'),
                    );
                    if (!dataJsonEntry) {
                        console.warn('Backup archive missing data.json, skipping');
                        continue;
                    }
                    const jsonText = new TextDecoder().decode(entries[dataJsonEntry]);
                    let parsed: Card[] = [];
                    try {
                        parsed = JSON.parse(jsonText) as Card[];
                    } catch (e) {
                        console.error('Invalid data.json in backup', e);
                        continue;
                    }

                    totalItems += parsed.reduce(
                        (acc, c) => acc + c.photos.length + (c.audio?.length ?? 0),
                        0,
                    );
                    perFileData.push({ cards: parsed, entries });
                }

                // Restore
                setRestoreStep('restoringMedia');
                let done = 0;
                for (const { cards, entries } of perFileData) {
                    for (const card of cards) {
                        // Photos
                        for (const photo of card.photos) {
                            const path = `cards/${photo.id}.jpg`;
                            const bytes = entries[path];
                            if (!bytes) { setSkippedCount((s) => s + 1); done++; continue; }
                            const dataUrl = await bytesToDataUrl(
                                bytes,
                                photo.mime ?? 'image/jpeg',
                            );
                            try {
                                await storeMedia(
                                    card.speciesId,
                                    'photo' as CardMediaType,
                                    dataUrl,
                                    photo.identifiedAs,
                                    photo.identifiedBy ?? 'automatically',
                                    { id: photo.id, timestamp: photo.timestamp },
                                );
                                setRestoredCount((c) => c + 1);
                            } catch (e) {
                                console.warn('Failed to restore photo', photo.id, e);
                                setSkippedCount((s) => s + 1);
                            }
                            done++;
                            setRestoreProgress(totalItems > 0 ? done / totalItems : 1);
                        }
                        // Audio
                        for (const audio of card.audio ?? []) {
                            // Attempt to preserve original extension; fall back to wav
                            const ext = audio.mime?.split('/')[1] ?? 'wav';
                            const path = `cards/${audio.id}.${ext}`;
                            let bytes = entries[path];
                            if (!bytes) {
                                // Fallback to expected default
                                bytes = entries[`cards/${audio.id}.wav`];
                            }
                            if (!bytes) { setSkippedCount((s) => s + 1); done++; continue; }
                            const dataUrl = await bytesToDataUrl(
                                bytes,
                                audio.mime ?? 'audio/wav',
                            );
                            try {
                                await storeMedia(
                                    card.speciesId,
                                    'audio' as CardMediaType,
                                    dataUrl,
                                    audio.identifiedAs ?? card.photos[0]?.identifiedAs ?? '',
                                    audio.identifiedBy ?? 'automatically',
                                    { id: audio.id, timestamp: audio.timestamp },
                                );
                                setRestoredCount((c) => c + 1);
                            } catch (e) {
                                console.warn('Failed to restore audio', audio.id, e);
                                setSkippedCount((s) => s + 1);
                            }
                            done++;
                            setRestoreProgress(totalItems > 0 ? done / totalItems : 1);
                        }
                    }
                }

                setRestoreStep('done');
                setRestoreProgress(1);
            } catch (e) {
                console.error('Restore failed', e);
                alert('Het terugzetten van de backup is mislukt.');
                setRestoreStep('none');
                setRestoreProgress(0);
            } finally {
                isRestoring.current = false;
            }
        },
        [bytesToDataUrl, storeMedia],
    );

    return {
        restoreStep,
        restoreProgress,
        restoredCount,
        skippedCount,
        restoreFromFiles,
    };
};

export default useRestore;
